import type { Scene, SceneObject } from '../types';
import type { Keyframe, KeyframeState, TimelineLayer } from '../types/timeline';

function cloneState(s: KeyframeState): KeyframeState {
  return { ...s };
}

function cloneObjectStates(
  os?: Record<string, KeyframeState>
): Record<string, KeyframeState> | undefined {
  if (!os || Object.keys(os).length === 0) return undefined;
  return Object.fromEntries(Object.entries(os).map(([k, v]) => [k, cloneState(v)]));
}

/** defaultPose (DB defaultState) varsa onu; yoksa mevcut sahne alanlarını kullan. */
export function sceneObjectRestPoseState(obj: SceneObject): KeyframeState {
  const p = obj.defaultPose;
  if (p) {
    return {
      x: p.x,
      y: p.y,
      width: p.width,
      height: p.height,
      rotation: p.rotation,
      opacity: p.opacity,
    };
  }
  return {
    x: obj.x,
    y: obj.y,
    width: obj.width,
    height: obj.height,
    rotation: obj.rotation,
    opacity: obj.opacity,
  };
}

/**
 * Yeni keyframe başlangıç state'i:
 * - Bu frame'den önce bir keyframe varsa onun state / objectStates kopyası
 * - Yoksa layer nesnelerinin defaultState (defaultPose) ile uyumlu pozları
 */
export function seedNewKeyframeState(
  layer: TimelineLayer,
  frame: number,
  layerKeyframes: Keyframe[],
  scene: Scene
): { state: KeyframeState; objectStates?: Record<string, KeyframeState> } {
  const prev = [...layerKeyframes]
    .filter((kf) => kf.frame < frame)
    .sort((a, b) => b.frame - a.frame)[0];

  if (prev) {
    return {
      state: cloneState(prev.state),
      objectStates: cloneObjectStates(prev.objectStates),
    };
  }

  const layerIdNum = Number(layer.id);
  const objectsInLayer = scene.objects.filter(
    (o) =>
      (o.layerId != null && Number(o.layerId) === layerIdNum) ||
      o.id === layer.objectId ||
      o.id === layer.id
  );

  if (objectsInLayer.length === 0) {
    return {
      state: {
        x: 0,
        y: 0,
        width: 100,
        height: 100,
        rotation: 0,
        opacity: 1,
      },
    };
  }

  if (objectsInLayer.length === 1) {
    return { state: sceneObjectRestPoseState(objectsInLayer[0]) };
  }

  const objectStates: Record<string, KeyframeState> = {};
  for (const obj of objectsInLayer) {
    objectStates[obj.id] = sceneObjectRestPoseState(obj);
  }

  const primaryId = layer.objectId;
  const state: KeyframeState =
    (primaryId ? objectStates[primaryId] : undefined) ?? objectStates[objectsInLayer[0].id];

  return { state, objectStates };
}
